import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalFullScreenStore from 'shared/ui/ModalFullScreen/store/modalFullScreenStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type AccessRequest } from 'entities/Users/model/AccessRequest'
import { AssignMemberToInbox } from 'pages/settings/pages/members/ui/AssignMemberToInbox'
import { AssignMemberToInboxStore } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/assignMemberToInboxStore'
import { InboxAccessFlow } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/flows'
import { RejectAccessRequestStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/RejectAccessRequestModal/store/rejectAccessRequestStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'

export class ReviewInboxAccessStore {
  readonly modalId = nanoid()
  private _assignMemberToInboxStore: AssignMemberToInboxStore

  constructor(
    private _entities: IEntitiesStore,
    private _settings: SettingsStore,
    private _onSuccess?: () => void
  ) {
    const flow = new InboxAccessFlow(
      () => this._assignMemberToInboxStore.selectedMember?.id ?? null,
      this.addMembersToExistingInbox,
      this.handleSuccess
    )

    this._assignMemberToInboxStore = new AssignMemberToInboxStore({
      entities: this._entities,
      settings: this._settings,
      mode: 'inboxAccess',
      onCancel: this.onClose,
      onReject: this.openRejectModal,
      flow,
    })
    makeAutoObservable(this)
  }

  private addMembersToExistingInbox = async (
    memberId: number,
    inboxIds: number[]
  ): Promise<boolean> => {
    return this._assignMemberToInboxStore.assignToExistingInboxStore.addMembersToExistingInbox(
      memberId,
      inboxIds
    )
  }

  openModal = (accessRequest: AccessRequest) => {
    const member = this._entities.usersStore.getItem(accessRequest.user.id)
    this._assignMemberToInboxStore.setAccessRequestData(accessRequest, member ?? undefined)

    modalFullScreenStore.addModal({
      id: this.modalId,
      title: 'Inbox access',
      ModalContent: AssignMemberToInbox,
      ModalContentProps: {
        store: this._assignMemberToInboxStore,
      },
      onClose: this.onClose,
    })
  }

  openRejectModal = () => {
    const accessRequest = this._assignMemberToInboxStore.accessRequest
    if (!accessRequest) return

    new RejectAccessRequestStore(() => {
      this._onSuccess?.()
      this.onClose()
    }).openModal(accessRequest)
  }

  handleSuccess = () => {
    this._onSuccess?.()
    this.onClose()
  }

  onClose = () => {
    this._assignMemberToInboxStore.reset()
    modalFullScreenStore.removeModal(this.modalId)
  }
}
